"use client";
// React 
import { useState, Suspense } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

// Next.js 
import Link from "next/link";

// Libraries
import { AlertCircle, Loader2 } from "lucide-react";
import toast from "react-hot-toast";

// Utilities
import { createClient } from "@/utils/supabase/client";

// Components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoogleSignInButton } from "@/components/auth-buttons";
import LoadingDots from "@/components/loading-dots";
import { Inputs } from "@/interfaces";
import { useSearchParams, useRouter } from "next/navigation";

function Login() {
  const searchParams = useSearchParams();

  const redirectTo = searchParams.get("redirectTo") || "/";

  const router = useRouter();

  //Auth
  const supabase = createClient();

  //States
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(null);

  //Auth handler
  const handleSignIn: SubmitHandler<Inputs> = async (form) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({
        email: form.email,
        password: form.password,
      });
      if (error) {
        throw error.message;
      } else {
        router.replace(redirectTo);
      }
    } catch (error: any) {
      setAuthError(error);
    }
    setLoading(false);
  };
  const handleSignInWithGoogle = async () => {
    try {
      setLoading(true);
      const redirectUrl = `${process.env.NEXT_PUBLIC_REDIRECT_URL}/auth/callback`;
      console.log(redirectUrl);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: redirectUrl,
        },
      });
      if (error) throw error;
    } catch (error: any) {
      setAuthError(error.message);
    } finally {
      setLoading(false);
    }
  };

  //react hook forms
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();

  return (
    <section className="bg-background">
      <div className="flex flex-col items-center justify-center px-4 py-8 mx-auto h-screen lg:py-0">
        <div className="w-full rounded-lg shadow border sm:max-w-sm lg:max-w-md ">
          <form onSubmit={handleSubmit(handleSignIn)}>
            <div className="p-6 space-y-6 sm:p-8">
              <h1 className="text-xl font-bold leading-tight tracking-tight sm:text-2xl text-primary">
                Sign in to your account
              </h1>
              {authError && (
                <div className="flex items-center gap-2 ">
                  <AlertCircle size={15} color="red" />
                  <Label htmlFor="name" className="text-md text-red-600">
                    {authError}
                  </Label>
                </div>
              )}
              <div>
                <Input
                  placeholder="Email Address"
                  {...register("email", { required: true })}
                  className="border text-primary sm:text-sm rounded-lg  block w-full p-2.5 "
                />
                {errors.email && (
                  <div className="flex items-center gap-2 mt-3">
                    <AlertCircle size={15} color="red" />
                    <Label htmlFor="name" className="text-md ">
                      Email is required
                    </Label>
                  </div>
                )}
              </div>
              <div>
                <Input
                  type="password"
                  placeholder="Password"
                  {...register("password", { required: true })}
                  className="border  sm:text-sm rounded-lg  block w-full p-2.5 "
                />
                <Link
                  href="/reset-password"
                  className="ml-auto inline-block text-sm underline px-2.5 float-end"
                >
                  Forgot your password?
                </Link>
                {errors.password && (
                  <div className="flex items-center gap-2 mt-3">
                    <AlertCircle size={15} color="red" />
                    <Label htmlFor="name" className="text-md text-red-600">
                      Password is required
                    </Label>
                  </div>
                )}
              </div>
              <Button
                disabled={loading}
                type="submit"
                className="w-full  focus:ring-4 focus:outline-none  font-medium rounded-lg text-sm px-5 py-3 text-center  "
              >
                {loading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                {loading ? "Authenticating" : "Authenticate"}
              </Button>
            </div>
          </form>
          <div className="relative">
            <div className="absolute inset-0 flex items-center ">
              <span className="w-full border-t bg-primary" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="px-2 bg-background">Or continue with</span>
            </div>
          </div>
          <div className="p-6 space-y-6 sm:p-8">
            <GoogleSignInButton handleClick={handleSignInWithGoogle} />
          </div>
        </div>
      </div>
    </section>
  );
}
export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <div className="flex justify-center items-center min-h-screen">
          <LoadingDots />
        </div>
      }
    >
      <Login />
    </Suspense>
  );
}
